/**
 * Error handling utility for converting API errors to user-friendly messages
 */

import { Injectable } from '@angular/core';
import { 
  AuthErrorResponse, 
  UserFriendlyError, 
  BusinessError 
} from '../types/auth.types';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {

  /**
   * Convert API error response to user-friendly error
   */
  convertToUserFriendlyError(error: any): UserFriendlyError {
    console.warn('🔄 Converting error to user-friendly message:', error);

    // Handle structured business errors from API
    if (this.isBusinessError(error)) {
      return this.handleBusinessError(error);
    }

    // Handle HTTP error responses
    if (error?.error && this.isBusinessError(error.error)) {
      return this.handleBusinessError(error.error);
    }

    // Handle HTTP status codes
    if (error?.status) {
      return this.handleHttpError(error);
    }

    // Handle network/connection errors
    if (error?.name === 'HttpErrorResponse' && error?.status === 0) {
      return {
        title: 'Connection Problem',
        message: 'Unable to connect to our servers. Please check your internet connection.',
        action: 'Please check your internet connection and try again',
        type: 'error',
        canRetry: true,
        retryAfter: 5
      };
    }

    // Handle Firebase Auth errors
    if (error?.code?.startsWith('auth/')) {
      return this.handleFirebaseAuthError(error);
    }

    // Generic error fallback
    return {
      title: 'Something Went Wrong',
      message: 'We encountered an unexpected issue. Please try again.',
      action: 'Please try again in a moment. If the problem persists, contact support.',
      type: 'error',
      canRetry: true
    };
  }

  /**
   * Check if error is a structured business error
   */
  private isBusinessError(error: any): error is BusinessError {
    return error && 
           typeof error === 'object' && 
           'errorCode' in error && 
           'userMessage' in error && 
           'userAction' in error;
  }

  /**
   * Handle structured business errors
   */
  private handleBusinessError(error: BusinessError): UserFriendlyError {
    const baseError: UserFriendlyError = {
      title: this.getErrorTitle(error.errorCode),
      message: error.userMessage,
      action: error.userAction,
      type: this.getErrorType(error.errorCode),
      canRetry: this.canRetry(error.errorCode)
    };

    // Add retry timing for service errors
    if ('retryAfter' in error && typeof error.retryAfter === 'number') {
      baseError.retryAfter = error.retryAfter;
    }

    return baseError;
  }

  /**
   * Handle HTTP errors
   */
  private handleHttpError(error: any): UserFriendlyError {
    switch (error.status) {
      case 400:
        return {
          title: 'Invalid Request',
          message: 'The information provided is not valid. Please check your input.',
          action: 'Please review the form and correct any errors',
          type: 'warning',
          canRetry: false
        };

      case 401:
        return {
          title: 'Authentication Required',
          message: 'Your session has expired. Please sign in again.',
          action: 'Please sign in again to continue',
          type: 'warning',
          canRetry: false
        };

      case 403:
        return {
          title: 'Access Denied',
          message: 'You don\'t have permission to perform this action.',
          action: 'Please contact support if you believe this is an error',
          type: 'error',
          canRetry: false
        };

      case 404:
        return {
          title: 'Not Found',
          message: 'The requested information could not be found.',
          action: 'Please check your information and try again',
          type: 'info',
          canRetry: false
        };

      case 409:
        return {
          title: 'Conflict',
          message: 'This information is already in use by another account.',
          action: 'Please try with different information or sign in if you already have an account',
          type: 'warning',
          canRetry: false
        };

      case 429:
        return {
          title: 'Too Many Requests',
          message: 'You\'ve made too many requests. Please wait before trying again.',
          action: 'Please wait a few minutes before trying again',
          type: 'warning',
          canRetry: true,
          retryAfter: 60
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          title: 'Server Error',
          message: 'We\'re experiencing technical difficulties on our end.',
          action: 'Please try again in a few minutes. If the problem continues, contact support.',
          type: 'error',
          canRetry: true,
          retryAfter: 30
        };

      default:
        return {
          title: 'Unexpected Error',
          message: 'An unexpected error occurred while processing your request.',
          action: 'Please try again. If the problem persists, contact support.',
          type: 'error',
          canRetry: true
        };
    }
  }

  /**
   * Handle Firebase Auth specific errors
   */
  private handleFirebaseAuthError(error: any): UserFriendlyError {
    switch (error.code) {
      case 'auth/user-not-found':
        return {
          title: 'Account Not Found',
          message: 'No account found with this information.',
          action: 'Please check your credentials or create a new account',
          type: 'info',
          canRetry: false
        };

      case 'auth/wrong-password':
        return {
          title: 'Incorrect Password',
          message: 'The password you entered is incorrect.',
          action: 'Please check your password and try again',
          type: 'warning',
          canRetry: false
        };

      case 'auth/invalid-email':
        return {
          title: 'Invalid Email',
          message: 'The email address format is not valid.',
          action: 'Please enter a valid email address',
          type: 'warning',
          canRetry: false
        };

      case 'auth/user-disabled':
        return {
          title: 'Account Disabled',
          message: 'This account has been disabled.',
          action: 'Please contact support for assistance',
          type: 'error',
          canRetry: false
        };

      case 'auth/too-many-requests':
        return {
          title: 'Too Many Attempts',
          message: 'Too many failed attempts. Please try again later.',
          action: 'Please wait before trying again',
          type: 'warning',
          canRetry: true,
          retryAfter: 300
        };

      case 'auth/network-request-failed':
        return {
          title: 'Network Error',
          message: 'Unable to connect. Please check your internet connection.',
          action: 'Please check your connection and try again',
          type: 'error',
          canRetry: true
        };

      default:
        return {
          title: 'Authentication Error',
          message: 'An authentication error occurred.',
          action: 'Please try again or contact support if the problem persists',
          type: 'error',
          canRetry: true
        };
    }
  }

  /**
   * Get appropriate title for error code
   */
  private getErrorTitle(errorCode: string): string {
    switch (errorCode) {
      case 'VALIDATION_ERROR':
        return 'Invalid Information';
      case 'AUTH_ERROR':
        return 'Authentication Failed';
      case 'TOKEN_EXPIRED':
        return 'Session Expired';
      case 'TOKEN_INVALID':
        return 'Invalid Session';
      case 'USER_ALREADY_EXISTS':
        return 'Account Already Exists';
      case 'USER_NOT_FOUND':
        return 'Account Not Found';
      case 'SERVICE_UNAVAILABLE':
        return 'Service Unavailable';
      case 'DATABASE_ERROR':
        return 'Technical Difficulty';
      case 'EXTERNAL_SERVICE_ERROR':
        return 'Service Error';
      default:
        return 'Error';
    }
  }

  /**
   * Get appropriate error type for styling
   */
  private getErrorType(errorCode: string): 'error' | 'warning' | 'info' {
    switch (errorCode) {
      case 'VALIDATION_ERROR':
      case 'USER_ALREADY_EXISTS':
      case 'TOKEN_EXPIRED':
        return 'warning';
      case 'USER_NOT_FOUND':
        return 'info';
      case 'AUTH_ERROR':
      case 'TOKEN_INVALID':
      case 'SERVICE_UNAVAILABLE':
      case 'DATABASE_ERROR':
      case 'EXTERNAL_SERVICE_ERROR':
      default:
        return 'error';
    }
  }

  /**
   * Determine if error allows retry
   */
  private canRetry(errorCode: string): boolean {
    switch (errorCode) {
      case 'SERVICE_UNAVAILABLE':
      case 'DATABASE_ERROR':
      case 'EXTERNAL_SERVICE_ERROR':
        return true;
      case 'VALIDATION_ERROR':
      case 'AUTH_ERROR':
      case 'TOKEN_EXPIRED':
      case 'TOKEN_INVALID':
      case 'USER_ALREADY_EXISTS':
      case 'USER_NOT_FOUND':
      default:
        return false;
    }
  }

  /**
   * Get user-friendly error message for display in UI
   */
  getErrorMessage(error: any): string {
    const userError = this.convertToUserFriendlyError(error);
    return `${userError.message} ${userError.action}`;
  }

  /**
   * Check if error indicates user should be redirected to login
   */
  shouldRedirectToLogin(error: any): boolean {
    if (this.isBusinessError(error) || this.isBusinessError(error?.error)) {
      const businessError = this.isBusinessError(error) ? error : error.error;
      return ['AUTH_ERROR', 'TOKEN_EXPIRED', 'TOKEN_INVALID'].includes(businessError.errorCode);
    }

    if (error?.status === 401) {
      return true;
    }

    if (error?.code?.startsWith('auth/')) {
      return ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-token'].includes(error.code);
    }

    return false;
  }
}
